import React, { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import { createBranch, createZone, deleteBranch, deleteZone, fetchOrgTree } from "utils/orgApi";
import { useAccountAccess } from "utils/access";

const inputClass =
  "mt-1 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const emptyForm = {
  name: "",
  branchCode: "",
  description: "",
  address: "",
  phone: "",
  email: "",
  gst: "",
  city: "",
  state: "",
  pincode: "",
  country: "",
  zoneId: "",
};

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
      <p className="text-sm text-navy-700 dark:text-white">{value || "—"}</p>
    </div>
  );
}

function TextInput({ label, name, value, onChange, required }) {
  return (
    <label className="block text-sm font-semibold text-navy-700 dark:text-white">
      {label}
      <input
        className={inputClass}
        name={name}
        value={value}
        required={required}
        onChange={onChange}
      />
    </label>
  );
}

function PlaceForm({ kind, zones, initialZoneId, busy, error, onClose, onSubmit }) {
  const [form, setForm] = useState({ ...emptyForm, zoneId: initialZoneId || "" });
  const change = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <form
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
      >
        <h3 className="text-xl font-bold text-navy-700 dark:text-white">
          {kind === "zone" ? "Add zone" : "Add branch"}
        </h3>
        <p className="mt-1 text-sm text-gray-500">This adds the place only. No login is created.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {kind === "branch" && (
            <label className="block text-sm font-semibold text-navy-700 dark:text-white sm:col-span-2">
              Zone
              <select className={inputClass} name="zoneId" value={form.zoneId} required onChange={change}>
                <option value="">Select a zone</option>
                {zones.map((zone) => (
                  <option key={zone.zoneId} value={zone.zoneId}>
                    {zone.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="sm:col-span-2">
            <TextInput label="Name" name="name" value={form.name} onChange={change} required />
          </div>
          {kind === "branch" && (
            <div className="sm:col-span-2">
              <TextInput
                label="Branch code"
                name="branchCode"
                value={form.branchCode}
                onChange={change}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Your own code for this branch. It is separate from the system branch id.
              </p>
            </div>
          )}
          <div className="sm:col-span-2">
            <TextInput label="Description" name="description" value={form.description} onChange={change} />
          </div>
          <div className="sm:col-span-2">
            <TextInput label="Address" name="address" value={form.address} onChange={change} />
          </div>
          <TextInput label="City" name="city" value={form.city} onChange={change} />
          <TextInput label="State" name="state" value={form.state} onChange={change} />
          <TextInput label="Pincode" name="pincode" value={form.pincode} onChange={change} />
          <TextInput label="Country" name="country" value={form.country} onChange={change} />
          <TextInput label="Phone" name="phone" value={form.phone} onChange={change} />
          <TextInput label="Email" name="email" value={form.email} onChange={change} />
          <TextInput label="GST" name="gst" value={form.gst} onChange={change} />
        </div>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-semibold text-gray-600 dark:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Saving" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmDelete({ name, busy, error, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white">Delete {name}?</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          This removes the place. It stays if it still has branches, devices, or login accounts.
        </p>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-semibold text-gray-600 dark:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Deleting" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ZoneBranches() {
  const access = useAccountAccess();
  const isAdmin = access?.accountType === "MASTER" || access?.accountType === "ADMIN" || access?.canManageUsers;
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [error, setError] = useState("");
  const [openBranch, setOpenBranch] = useState(null);
  const [formKind, setFormKind] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async (preferZoneId) => {
    const data = await fetchOrgTree();
    const list = data.zones || [];
    setZones(list);
    setZoneId((current) => {
      const preferred = preferZoneId != null ? String(preferZoneId) : String(current);
      if (list.some((item) => String(item.zoneId) === preferred)) return preferred;
      return list[0] ? String(list[0].zoneId) : "";
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchOrgTree()
      .then((data) => {
        if (cancelled) return;
        const list = data.zones || [];
        setZones(list);
        if (list[0]) setZoneId(String(list[0].zoneId));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load the organization.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const zone = zones.find((item) => String(item.zoneId) === String(zoneId));

  const submitPlace = async (form) => {
    setBusy(true);
    setActionError("");
    try {
      if (formKind === "zone") {
        const created = await createZone(form);
        setFormKind(null);
        await load(created.zoneId);
      } else {
        const created = await createBranch(form);
        setFormKind(null);
        await load(created.zoneId || form.zoneId);
      }
    } catch (err) {
      setActionError(err.message || "The place was not saved.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    setActionError("");
    try {
      if (confirm.kind === "zone") {
        await deleteZone(confirm.id);
        setConfirm(null);
        setOpenBranch(null);
        await load("");
      } else {
        await deleteBranch(confirm.id);
        setConfirm(null);
        setOpenBranch(null);
        await load(zoneId);
      }
    } catch (err) {
      setActionError(err.message || "The place was not deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-4">
      <Card extra="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Zone branches</h2>
            <p className="mt-1 text-sm text-gray-600">
              Branches under one zone of this account, with address, phone, email, contacts, and devices.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setActionError("");
                  setFormKind("zone");
                }}
                className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white"
              >
                Add zone
              </button>
              <button
                type="button"
                disabled={!zones.length}
                onClick={() => {
                  setActionError("");
                  setFormKind("branch");
                }}
                className="rounded-md border border-brand-500 px-4 py-2 text-sm font-semibold text-brand-500 disabled:opacity-50"
              >
                Add branch
              </button>
            </div>
          )}
        </div>
        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
        {zones.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="block text-sm font-semibold text-navy-700 dark:text-white">
              Zone
              <select
                className="mt-1 block w-full min-w-[220px] rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900"
                value={zoneId}
                onChange={(event) => {
                  setZoneId(event.target.value);
                  setOpenBranch(null);
                }}
              >
                {zones.map((item) => (
                  <option key={item.zoneId} value={item.zoneId}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            {isAdmin && zone && (
              <button
                type="button"
                onClick={() => {
                  setActionError("");
                  setConfirm({ kind: "zone", id: zone.zoneId, name: zone.name });
                }}
                className="rounded-md px-3 py-2 text-sm font-semibold text-red-500"
              >
                Delete zone
              </button>
            )}
          </div>
        ) : (
          !error && <p className="mt-4 text-sm text-gray-500">No zones yet.</p>
        )}
      </Card>

      {zone && (
        <div className="grid grid-cols-1 gap-4">
          {zone.branches.length === 0 && (
            <p className="text-sm text-gray-500">No branches in this zone.</p>
          )}
          {zone.branches.map((branch) => {
            const open = openBranch === branch.branchId;
            return (
              <Card key={branch.branchId} extra="p-5">
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    className="flex-1 text-left text-lg font-bold text-navy-700 dark:text-white"
                    onClick={() => setOpenBranch(open ? null : branch.branchId)}
                  >
                    {branch.name}
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setActionError("");
                        setConfirm({ kind: "branch", id: branch.branchId, name: branch.name });
                      }}
                      className="text-sm font-semibold text-red-500"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-sm text-gray-500"
                    onClick={() => setOpenBranch(open ? null : branch.branchId)}
                  >
                    {open ? "Hide devices" : "Show devices"}
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="Branch code" value={branch.branchCode} />
                  <Field label="Address" value={[branch.address, branch.city, branch.state, branch.pincode].filter(Boolean).join(", ")} />
                  <Field label="Phone" value={branch.phone} />
                  <Field label="Email" value={branch.email} />
                  <Field
                    label="Contact personnel"
                    value={
                      branch.contacts.length
                        ? branch.contacts
                            .map((person) =>
                              [person.displayName, person.email, person.phone].filter(Boolean).join(" · ")
                            )
                            .join("; ")
                        : ""
                    }
                  />
                </div>
                {open && (
                  <div className="mt-4 border-t border-gray-200 pt-3 dark:border-white/10">
                    <p className="text-sm font-semibold text-navy-700 dark:text-white">Devices</p>
                    {branch.devices.length === 0 ? (
                      <p className="mt-1 text-sm text-gray-500">No devices recorded</p>
                    ) : (
                      <ul className="mt-2 space-y-1">
                        {branch.devices.map((device) => (
                          <li key={device.did} className="text-sm text-navy-700 dark:text-white">
                            {device.aid || "No asset id"} · DID {device.did}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {formKind && (
        <PlaceForm
          kind={formKind}
          zones={zones}
          initialZoneId={zoneId}
          busy={busy}
          error={actionError}
          onClose={() => {
            if (!busy) setFormKind(null);
          }}
          onSubmit={submitPlace}
        />
      )}
      {confirm && (
        <ConfirmDelete
          name={confirm.name}
          busy={busy}
          error={actionError}
          onClose={() => {
            if (!busy) setConfirm(null);
          }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
